import React, { useEffect, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { getFilesStatisticsRoutine } from 'ducks/files/actions'
import {
  selectFileStatistics,
  selectFileStatisticsLoading
} from 'ducks/files/selectors'
import CloseIcon from '@mui/icons-material/Close'
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import SyncIcon from '@mui/icons-material/Sync'

const formatBytes = bytes => {
  if (!bytes || bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
}

const statusLabels = {
  new: 'Queued',
  processing: 'Processing',
  lambdaProcessing: 'Processing',
  waiting_for_ai: 'AI Analysis' // eslint-disable-line camelcase
}

const PREVIEW_COUNT = 5

const friendlyError = raw => {
  if (!raw) return 'File may have timed out, please retry'
  const lower = raw.toLowerCase()
  if (lower.includes('format not supported') || lower.includes('unsupported'))
    return 'Format not supported'
  return 'File may have timed out, please retry'
}

const FileInfoPanel = ({ isOpen, onClose, bpId }) => {
  const dispatch = useDispatch()
  const statistics = useSelector(selectFileStatistics)
  const loading = useSelector(selectFileStatisticsLoading)
  const [animate, setAnimate] = useState(false)
  const [showAllQueue, setShowAllQueue] = useState(false)
  const [showFailed, setShowFailed] = useState(false)

  useEffect(() => {
    if (isOpen && bpId) {
      setAnimate(false)
      setShowAllQueue(false)
      setShowFailed(false)
      dispatch(getFilesStatisticsRoutine({ id: bpId }))
    }
  }, [isOpen, bpId, dispatch])

  useEffect(() => {
    if (!loading && statistics && isOpen) {
      const timer = setTimeout(() => setAnimate(true), 50)
      return () => clearTimeout(timer)
    }
  }, [loading, statistics, isOpen])

  if (!isOpen) return null

  const data = statistics || {}
  const formatDist = data.formatDistribution || {}
  const sizeDist = data.sizeDistribution || {}
  const uploadQueue = data.uploadQueue || {}
  const sharepoint = data.sharepoint || {}

  const inProgress = uploadQueue.inProgress || []
  const failed = uploadQueue.failed || []
  const hasQueue = inProgress.length > 0 || failed.length > 0

  const sortedFormats = Object.entries(formatDist)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10)
  const maxFormatCount = Math.max(...sortedFormats.map(e => e[1]), 1)

  const sizeLabels = {
    under1mb: '< 1 MB',
    from1to10mb: '1-10 MB',
    from10to50mb: '10-50 MB',
    from50to100mb: '50-100 MB',
    from100to1gb: '100 MB-1 GB',
    over1gb: '> 1 GB'
  }
  const maxSizeCount = Math.max(
    ...Object.values(sizeDist).map(Number),
    1
  )

  const visibleQueue = showAllQueue ? inProgress : inProgress.slice(0, PREVIEW_COUNT)
  const visibleFailed = showFailed ? failed : []

  return (
    <Overlay onClick={onClose}>
      <Panel onClick={e => e.stopPropagation()}>
        <DragHandle />
        <PanelHeader>
          <PanelTitle>File Information</PanelTitle>
          <CloseButton onClick={onClose}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </CloseButton>
        </PanelHeader>

        {loading ? (
          <PanelContent>
            <SummaryRow>
              <SummaryItem>
                <SkeletonLine $w='60px' $h='12px' />
                <SkeletonLine $w='48px' $h='24px' $mt='6px' />
              </SummaryItem>
              <SummaryItem>
                <SkeletonLine $w='80px' $h='12px' />
                <SkeletonLine $w='64px' $h='24px' $mt='6px' />
              </SummaryItem>
            </SummaryRow>
            {[1, 2, 3, 4, 5].map(i => (
              <SkeletonBar key={i}>
                <SkeletonLine $w='40px' $h='12px' />
                <SkeletonLine $w='100%' $h='16px' $radius='4px' />
                <SkeletonLine $w='28px' $h='12px' />
              </SkeletonBar>
            ))}
          </PanelContent>
        ) : (
          <PanelContent>
            <SummaryRow>
              <SummaryItem>
                <SummaryLabel>Total Files</SummaryLabel>
                <SummaryValue>{data.totalFiles ?? 0}</SummaryValue>
              </SummaryItem>
              <SummaryItem>
                <SummaryLabel>Total Storage</SummaryLabel>
                <SummaryValue>
                  {formatBytes(data.totalStorageBytes)}
                </SummaryValue>
              </SummaryItem>
            </SummaryRow>

            {sortedFormats.length > 0 && (
              <Section>
                <SectionTitle>Format Distribution</SectionTitle>
                {sortedFormats.map(([ext, count], idx) => (
                  <BarRow key={ext}>
                    <BarLabel>{(ext || 'other').toUpperCase()}</BarLabel>
                    <BarTrack>
                      <BarFill
                        style={{
                          width: animate
                            ? `${(count / maxFormatCount) * 100}%`
                            : '0%',
                          transitionDelay: `${idx * 40}ms`
                        }}
                      />
                    </BarTrack>
                    <BarCount>{count}</BarCount>
                  </BarRow>
                ))}
              </Section>
            )}

            <Section>
              <SectionTitle>Size Distribution</SectionTitle>
              {Object.entries(sizeLabels).map(([key, label], idx) => {
                const count = sizeDist[key] || 0
                return (
                  <BarRow key={key}>
                    <BarLabel $wide>{label}</BarLabel>
                    <BarTrack>
                      <BarFill
                        $color='#6366F1'
                        style={{
                          width: animate
                            ? `${(count / maxSizeCount) * 100}%`
                            : '0%',
                          transitionDelay: `${(sortedFormats.length + idx) * 40}ms`
                        }}
                      />
                    </BarTrack>
                    <BarCount>{count}</BarCount>
                  </BarRow>
                )
              })}
            </Section>

            {hasQueue && (
              <Section>
                <SectionTitle>
                  <CloudUploadOutlinedIcon
                    sx={{ fontSize: 16, marginRight: '4px' }}
                  />
                  Upload Queue
                </SectionTitle>

                {inProgress.length > 0 && (
                  <>
                    <QueueDescription>
                      {inProgress.length} file
                      {inProgress.length !== 1 ? 's' : ''} being processed
                    </QueueDescription>
                    {visibleQueue.map(file => (
                      <QueueItem key={file.id}>
                        <QueueName>{file.displayName}</QueueName>
                        <StatusBadge $status={file.status}>
                          {statusLabels[file.status] || file.status}
                        </StatusBadge>
                        <QueueSize>
                          {formatBytes(file.size)}
                        </QueueSize>
                      </QueueItem>
                    ))}
                    {inProgress.length > PREVIEW_COUNT && (
                      <ViewAllButton
                        onClick={() => setShowAllQueue(v => !v)}
                      >
                        {showAllQueue
                          ? 'Show less'
                          : `View all ${inProgress.length} files`}
                        <ArrowIcon $open={showAllQueue}>
                          <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
                        </ArrowIcon>
                      </ViewAllButton>
                    )}
                  </>
                )}

                {failed.length > 0 && (
                  <>
                    <FailedHeader
                      onClick={() => setShowFailed(v => !v)}
                    >
                      <FailedHeaderLeft>
                        <ErrorOutlineIcon
                          sx={{ fontSize: 14, color: '#EF4444' }}
                        />
                        <span>
                          {failed.length} upload
                          {failed.length !== 1 ? 's' : ''} failed
                        </span>
                      </FailedHeaderLeft>
                      <ViewAllButton as='span'>
                        {showFailed ? 'Hide' : 'View all'}
                        <ArrowIcon $open={showFailed}>
                          <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
                        </ArrowIcon>
                      </ViewAllButton>
                    </FailedHeader>
                    {showFailed && (
                      <FailedList>
                        {failed.map(file => (
                          <FailedItem key={file.id}>
                            <FailedName>{file.displayName}</FailedName>
                            <FailedReason>{friendlyError(file.error)}</FailedReason>
                          </FailedItem>
                        ))}
                      </FailedList>
                    )}
                  </>
                )}
              </Section>
            )}

            {sharepoint.total > 0 && (
              <Section>
                <SectionTitle>
                  <SyncIcon sx={{ fontSize: 16, marginRight: '4px' }} />
                  SharePoint Sync
                </SectionTitle>
                <SyncStats>
                  <SyncNumber>{sharepoint.synced}</SyncNumber>
                  <SyncLabel>
                    of {sharepoint.total} synced
                  </SyncLabel>
                </SyncStats>
                <SyncTrack>
                  <SyncFill
                    style={{
                      width: animate
                        ? `${(sharepoint.synced / sharepoint.total) * 100}%`
                        : '0%'
                    }}
                  />
                </SyncTrack>
                {sharepoint.unsynced > 0 && (
                  <SyncPending>
                    {sharepoint.unsynced} file
                    {sharepoint.unsynced !== 1 ? 's' : ''} pending sync
                  </SyncPending>
                )}
              </Section>
            )}
          </PanelContent>
        )}
      </Panel>
    </Overlay>
  )
}

export default FileInfoPanel

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`

const slideUp = keyframes`
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
`

const shimmer = keyframes`
  0% { background-position: -200px 0; }
  100% { background-position: 200px 0; }
`

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1300;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 40px;
  animation: ${fadeIn} 0.2s ease;

  @media (max-width: 768px) {
    padding-top: 0;
    align-items: flex-end;
  }
`

const Panel = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  width: 440px;
  height: 85vh;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 768px) {
    width: 100%;
    height: 92vh;
    max-height: 92vh;
    border-radius: 16px 16px 0 0;
    animation: ${slideUp} 0.25s ease-out;
  }

  @supports (height: 85dvh) {
    height: 85dvh;
    max-height: 85dvh;

    @media (max-width: 768px) {
      height: 92dvh;
      max-height: 92dvh;
    }
  }
`

const DragHandle = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    justify-content: center;
    padding: 10px 0 2px;
    flex-shrink: 0;

    &::after {
      content: '';
      width: 36px;
      height: 4px;
      background: #d1d5db;
      border-radius: 2px;
    }
  }
`

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
`

const PanelTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #111827;
`

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  color: #6b7280;
  display: flex;
  align-items: center;

  &:hover {
    background: #f3f4f6;
    color: #111827;
  }

  @media (max-width: 768px) {
    padding: 8px;
  }
`

const PanelContent = styled.div`
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto;
  padding: 16px 20px;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;

  @media (max-width: 768px) {
    padding-bottom: calc(60px + env(safe-area-inset-bottom, 0px));
  }
`

const SkeletonLine = styled.div`
  width: ${p => p.$w || '100%'};
  height: ${p => p.$h || '14px'};
  border-radius: ${p => p.$radius || '3px'};
  margin-top: ${p => p.$mt || '0'};
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 400px 100%;
  animation: ${shimmer} 1.5s infinite;
`

const SkeletonBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
`

const SummaryRow = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
`

const SummaryItem = styled.div`
  flex: 1;
  background: #f9fafb;
  border-radius: 8px;
  padding: 12px;
`

const SummaryLabel = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 4px;
`

const SummaryValue = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: #111827;
`

const Section = styled.div`
  margin-bottom: 20px;
`

const SectionTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
`

const BarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
`

const BarLabel = styled.span`
  font-size: 12px;
  color: #6b7280;
  width: ${props => (props.$wide ? '90px' : '50px')};
  flex-shrink: 0;
  text-align: right;
`

const BarTrack = styled.div`
  flex: 1;
  height: 16px;
  background: #f3f4f6;
  border-radius: 4px;
  overflow: hidden;
`

const BarFill = styled.div`
  height: 100%;
  background: ${props => props.$color || '#33becc'};
  border-radius: 4px;
  min-width: ${props => (props.style?.width !== '0%' ? '2px' : '0')};
  transition: width 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
`

const BarCount = styled.span`
  font-size: 12px;
  color: #374151;
  width: 36px;
  text-align: right;
  flex-shrink: 0;
`

const QueueDescription = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 6px;
`

const QueueItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 0;
  font-size: 12px;
  color: #374151;
`

const QueueName = styled.span`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const StatusBadge = styled.span`
  font-size: 10px;
  font-weight: 500;
  padding: 1px 6px;
  border-radius: 10px;
  flex-shrink: 0;
  background: ${p =>
    p.$status === 'processing' || p.$status === 'lambdaProcessing'
      ? '#FEF3C7'
      : p.$status === 'waiting_for_ai'
        ? '#EDE9FE'
        : '#F3F4F6'};
  color: ${p =>
    p.$status === 'processing' || p.$status === 'lambdaProcessing'
      ? '#92400E'
      : p.$status === 'waiting_for_ai'
        ? '#5B21B6'
        : '#6B7280'};
`

const QueueSize = styled.span`
  color: #9ca3af;
  flex-shrink: 0;
`

const ViewAllButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 0;
  font-size: 12px;
  font-weight: 500;
  color: #6366f1;
  display: inline-flex;
  align-items: center;
  gap: 2px;

  &:hover {
    color: #4f46e5;
  }
`

const ArrowIcon = styled.span`
  display: inline-flex;
  transition: transform 0.2s ease;
  transform: rotate(${p => (p.$open ? '180deg' : '0deg')});
`

const FailedHeader = styled.button`
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  padding: 8px 10px;
  margin-top: 8px;
  width: 100%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;

  &:hover {
    background: #fee2e2;
  }
`

const FailedHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 500;
  color: #ef4444;
`

const FailedList = styled.div`
  border: 1px solid #fecaca;
  border-top: none;
  border-radius: 0 0 6px 6px;
  max-height: 200px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`

const FailedItem = styled.div`
  padding: 8px 10px;
  border-bottom: 1px solid #fef2f2;

  &:last-child {
    border-bottom: none;
  }
`

const FailedName = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: #374151;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const FailedReason = styled.div`
  font-size: 11px;
  color: #9ca3af;
  margin-top: 2px;
`

const SyncStats = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin-bottom: 8px;
`

const SyncNumber = styled.span`
  font-size: 22px;
  font-weight: 600;
  color: #111827;
`

const SyncLabel = styled.span`
  font-size: 13px;
  color: #6b7280;
`

const SyncTrack = styled.div`
  height: 8px;
  background: #f3f4f6;
  border-radius: 4px;
  overflow: hidden;
`

const SyncFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #10b981, #34d399);
  border-radius: 4px;
  transition: width 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
`

const SyncPending = styled.div`
  font-size: 12px;
  color: #f59e0b;
  margin-top: 6px;
`
